import WbsItemApi, { WbsItemDetail } from '../../../lib/functions/wbsItem'
import { isPermanentLookupFailure } from './basicByCodeCache'

// ホバーカードの詳細情報（ステータス・工数・担当者・予定期間など）は、チップの描画時点では
// 取得しない。本文に並ぶチップの数だけ getDetail が飛ぶ事態を避けるため、実際にホバーされた
// ときに初めて取得する（呼び出し側は useWbsItemDetailOnHover）。取得後はコード単位でキャッシュし、
// 2回目以降のホバーは即表示にする。
//
// 失敗時のキャッシュ方針は basicByCodeCache と同じ（isPermanentLookupFailure を共有）。
// キャッシュ本体を createBasicByCodeCache に寄せていないのは、取得がコードではなく uuid で
// 行われ（キーと取得の引数が別）、共通化すると引数の受け渡しだけのために型が複雑になるため。
// 404・権限エラーは再取得しても結論が変わらないためキャッシュし、それ以外（一時的なサーバ
// エラー・ネットワーク断）はキャッシュせず、次にホバーされたときにやり直せるようにする。
const cache = new Map<string, WbsItemDetail | null>()
const inFlight = new Map<string, Promise<WbsItemDetail | null>>()

export const peekCachedWbsItemDetail = (
  code: string
): WbsItemDetail | null | undefined => cache.get(code)

export const loadWbsItemDetail = (
  code: string,
  uuid: string
): Promise<WbsItemDetail | null> => {
  if (cache.has(code)) {
    return Promise.resolve(cache.get(code) ?? null)
  }
  const pending = inFlight.get(code)
  if (pending) {
    return pending
  }
  const request = WbsItemApi.getDetail({ uuid })
    .then(response => {
      const detail = response.json as WbsItemDetail
      cache.set(code, detail)
      return detail
    })
    .catch(error => {
      // ホバーカードの詳細が引けなかっただけであり、全画面トーストを出す話ではない
      // （globalErrorHandler の unhandledrejection 拾い上げを避けるため、ここで確実に catch する）。
      console.warn('Failed to fetch wbs item detail for a hover card.', {
        code,
        error,
      })
      if (isPermanentLookupFailure(error)) {
        cache.set(code, null)
      }
      return null
    })
    .finally(() => {
      inFlight.delete(code)
    })
  inFlight.set(code, request)
  return request
}
